import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import type { ScheduleDateChange, SchedulePreview } from "@/domains/pm/types";
import { fill } from "@/lib/i18n/fill";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { applyScheduleBatch, previewScheduleBatch } from "@/stores/pm/repository";
import {
  toDateChanges,
  toEdgeChanges,
  type useStagedSchedule,
} from "@/domains/pm/hooks/useStagedSchedule";
import type { ScheduleStagingApi } from "./scheduleStagingContext";

/**
 * The Confirm round trip: preview, decide, apply.
 *
 * It lives beside the buffer it settles, which the PAGE owns — the view tabs
 * and the unsaved-changes bar both sit above the staging provider in the tree,
 * so neither could reach this if the provider kept it. The provider is left
 * publishing the two contexts and nothing else.
 *
 * `buildingProjectId` may be null ("All projects"), in which case there is no
 * one project to schedule and confirm is a no-op — the Gantt does not stage in
 * that case either.
 */
export function useScheduleConfirm(
  buildingId: number,
  buildingProjectId: number | null,
  buffer: ReturnType<typeof useStagedSchedule>
): {
  staging: ScheduleStagingApi;
  /** The cascade the server predicts, awaiting the user's decision. */
  pending: SchedulePreview | null;
  resolve: (cascade: boolean) => void;
  cancel: () => void;
} {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const queryClient = useQueryClient();
  const { staged, stageEdgeAdd, stageEdgeEdit, stageEdgeDrop, discard } = buffer;
  const [pending, setPending] = useState<SchedulePreview | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * Every issue this apply moved, and where it was before.
   *
   * The RPC's own result carries `old_start`/`old_due` on each move, so undoing
   * needs no snapshot taken beforehand — the server already told us what it
   * changed. `targets` are the issues the user placed by hand; `moved` are the
   * ones the cascade carried with them, and both have to go back.
   */
  const previousDates = (result: SchedulePreview): ScheduleDateChange[] => {
    const moves = [
      ...(result.targets ?? []),
      ...(result.target ? [result.target] : []),
      ...result.moved,
    ];
    const byIssue = new Map<number, ScheduleDateChange>();
    for (const m of moves) {
      // A move that changed nothing is not worth writing back.
      if (m.old_start === m.new_start && m.old_due === m.new_due) continue;
      // First entry wins: a targets/moved overlap describes one issue twice,
      // and the target's `old_*` is the one before the user touched it.
      if (!byIssue.has(m.issue_id)) {
        byIssue.set(m.issue_id, {
          issue_id: m.issue_id,
          start_date: m.old_start,
          due_date: m.old_due,
        });
      }
    }
    return [...byIssue.values()];
  };

  const undo = useCallback(
    async (changes: ScheduleDateChange[]) => {
      if (buildingProjectId == null) return;
      try {
        // NOT cascaded: these are the exact dates the schedule had, so letting
        // the engine relax them again would land somewhere else entirely.
        await applyScheduleBatch(buildingProjectId, changes, null, false);
        await issuesStore.loadIssues(buildingId);
        toast.success(t.pm.issues.schedule.undone);
      } catch (e) {
        toast.error(friendlyIssueError(e, t));
      }
    },
    [buildingId, buildingProjectId, issuesStore, t]
  );

  const offerUndo = useCallback(
    (result: SchedulePreview) => {
      const changes = previousDates(result);
      if (changes.length === 0) return;
      toast.success(
        changes.length === 1
          ? t.pm.issues.schedule.appliedOne
          : fill(t.pm.issues.schedule.applied, { count: String(changes.length) }),
        {
          // The cascade can move issues the user never touched, so the way back
          // has to be one click and not a hunt through the chart.
          action: { label: t.pm.issues.schedule.undo, onClick: () => void undo(changes) },
          duration: 10_000,
        }
      );
    },
    [t, undo]
  );

  const settle = useCallback(
    async (cascade: boolean) => {
      if (buildingProjectId == null) return;
      setSaving(true);
      try {
        const result = await applyScheduleBatch(
          buildingProjectId,
          toDateChanges(staged),
          toEdgeChanges(staged),
          cascade
        );
        discard();
        setPending(null);
        await issuesStore.loadIssues(buildingId);
        await queryClient.invalidateQueries({
          queryKey: ["pm-issue-dependencies", buildingId],
        });
        offerUndo(result);
      } catch (e) {
        toast.error(friendlyIssueError(e, t));
        // The buffer is deliberately kept: the arrangement is the user's work,
        // and throwing it away because one write failed would be the worst
        // possible response to a transient error.
      } finally {
        setSaving(false);
      }
    },
    [buildingId, buildingProjectId, discard, issuesStore, offerUndo, queryClient, staged, t]
  );

  const confirm = useCallback(async () => {
    if (!staged.isDirty || buildingProjectId == null) return;
    setSaving(true);
    let preview: SchedulePreview;
    try {
      preview = await previewScheduleBatch(
        buildingProjectId,
        toDateChanges(staged),
        toEdgeChanges(staged)
      );
    } catch (e) {
      toast.error(friendlyIssueError(e, t));
      setSaving(false);
      return;
    }
    setSaving(false);

    // Nothing else is affected, so there is nothing to decide.
    if (preview.moved.length === 0 && preview.warnings.length === 0) {
      await settle(true);
      return;
    }
    setPending(preview);
  }, [buildingProjectId, settle, staged, t]);

  const staging = useMemo<ScheduleStagingApi>(
    () => ({
      staged,
      stageEdgeAdd,
      stageEdgeEdit,
      stageEdgeDrop,
      confirm: () => void confirm(),
      discard,
      saving,
    }),
    [staged, stageEdgeAdd, stageEdgeEdit, stageEdgeDrop, confirm, discard, saving]
  );

  return {
    staging,
    pending,
    resolve: (cascade: boolean) => void settle(cascade),
    cancel: () => setPending(null),
  };
}
